import React, { MutableRefObject } from 'react';
import styled from 'styled-components';
import {
  spacer4,
  spacer8,
  spacer16,
  spacer20,
  spacer24,
  spacer40,
  screenSmMax,
  gray75,
} from '@spotify-internal/encore-foundation';

import { hexToRgba } from '@mrkt/features/color-conversion';
import { ButtonTertiary, Type } from '@spotify-internal/encore-web';
import { useSwipeable } from 'react-swipeable';
import { ModuleType } from '../index';
import { ContentfulAsset } from '../../Contentful';
import {
  Crossfade,
  DELAYS,
  HeadlineWithSupertextComponent,
  ImageAspectBox21x9,
  withAnimationContext,
} from '../../ui';
import { Testimonial } from '../SingleTestimonialModule/Testimonial';
import { ContentfulImage } from '../../Contentful/ContentfulImage';
import { getTallest, setTallestClass } from '../../utils';
import { fadeInAnimation } from '../../ui/Animations';

const GroupTestimonial = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${spacer40} 0px 0px 0px;
  justify-content: space-between;

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding: ${spacer20} 0px;
  }
`;

const Left = styled.div`
  width: 35%;
  text-align: left;

  @media (max-width: ${screenSmMax}) {
    width: 100%;
  }
`;

const NameList = styled.div`
  display: flex;
  flex-direction: column;

  @media (max-width: ${screenSmMax}) {
    flex-direction: column;
    padding: ${spacer20} 0px;
    display: none;
  }
`;

const CurrentSource = withAnimationContext(
  styled(Type.h1).attrs({
    variant: Type.heading1,
    weight: Type.bold,
  })`
    ${fadeInAnimation(DELAYS.SECOND)};
    padding-bottom: ${spacer24};

    @media (min-width: ${screenSmMax}) {
      padding-top: ${spacer16};
    }
  `,
);

const Right = styled.div`
  width: 60%;
  padding-left: ${spacer16};

  @media (max-width: ${screenSmMax}) {
    width: 100%;
    align-items: center;
    padding-left: 0;
    padding-top: ${spacer16};
  }
`;

const Name = withAnimationContext(
  styled(ButtonTertiary)`
    ${fadeInAnimation(DELAYS.THIRD)};
    text-align: left;
    padding: ${spacer8} 0;

    &:hover:not(:focus):not(:disabled) {
      color: ${({ theme }) => hexToRgba(theme.box.text, 0.25)};
    }

    &.active {
      display: none;
    }
  `,
);

export const Dots = styled.div`
  display: none;
  justify-content: center;
  flex-direction: row;

  @media (max-width: ${screenSmMax}) {
    display: flex;
    justify-content: center;
    padding: 0px;
  }
`;

const ImageContainer = styled(ImageAspectBox21x9)``;

const TestimonialContainer = styled.div<{ tallest: number }>`
  position: relative;
  overflow: hidden;
  height: ${({ tallest }) => (tallest > 0 ? tallest : 250)}px;
`;

const CrossfadeImage = styled.div<{ active: boolean }>`
  overflow: hidden;
  position: absolute;
  height: 100%;
  width: 100%;

  ${Crossfade}
`;

const CrossfadeText = styled.div<{ active: boolean }>`
  position: absolute;

  ${Crossfade}
`;

export const Dot = styled.div<{ active: boolean; color?: string }>`
  display: none;
  border-radius: 50%;
  width: ${spacer8};
  height: ${spacer8};
  margin-left: ${spacer4};
  margin-right: ${spacer4};
  background: ${gray75};

  &.active {
    background: ${({ theme }) => theme.box.text};
  }
  @media (max-width: ${screenSmMax}) {
    display: flex;
  }

  &:hover {
    cursor: pointer;
  }
`;

const testimonialRefs: Array<React.MutableRefObject<any>> = [];

const getTestimonials = (
  activeIndex: number,
  testimonials: Array<TestimonalSlidesType>,
) => {
  return testimonials.map((testimonial, index) => {
    testimonialRefs[index] = React.useRef<MutableRefObject<HTMLDivElement>>();
    return (
      <CrossfadeText
        ref={testimonialRefs[index]}
        key={`testimonial-text-${index}`}
        active={activeIndex === index}
      >
        <Testimonial
          quote={testimonial.quote}
          source={testimonial.source}
          jobTitle={testimonial.jobTitle}
        />
      </CrossfadeText>
    );
  });
};

type TestimonalSlidesType = {
  quote?: string;
  image?: ContentfulAsset;
  caption?: string;
  source?: string;
  jobTitle?: string;
};

type GroupTestimonialModuleType = ModuleType & {
  testimonials?: TestimonalSlidesType[];
};

const getCurrentSource = (list: Array<TestimonalSlidesType>, index: number) => {
  return list[index]?.source;
};

export const GroupTestimonialModule = React.forwardRef<
  React.MutableRefObject<ModuleType>,
  GroupTestimonialModuleType
>(({ testimonials = [] }, ref) => {
  const [activeIndex, setActiveIndex] = React.useState(0);

  const nextIndex = (i: number) => (i + 1 >= testimonials.length ? 0 : i + 1);
  const prevIndex = (i: number) =>
    i - 1 >= 0 ? i - 1 : testimonials.length - 1;

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => jumpToTestimonial(nextIndex(activeIndex)),
    onSwipedRight: () => jumpToTestimonial(prevIndex(activeIndex)),
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
    trackTouch: true,
  });

  function jumpToTestimonial(index: number) {
    setActiveIndex(index);
  }

  // Begin dynamic height

  const [tallest, setTallest] = React.useState(0);
  const [width, setWidth] = React.useState(0);

  React.useEffect(() => {
    setTallest(getTallest(testimonialRefs));
    setTallestClass(testimonialRefs);
  }, [getTallest(testimonialRefs), width]);

  React.useEffect(() => {
    const handleResize = () => {
      setWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return function cleanupListener() {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  // end dynamic height

  return (
    <GroupTestimonial {...swipeHandlers}>
      <ImageContainer>
        {testimonials.map(
          (testimonial, index) =>
            testimonial.image && (
              <CrossfadeImage
                key={`testimonial-image-${index}`}
                active={activeIndex === index}
              >
                <ContentfulImage
                  asset={testimonial.image}
                  caption={testimonial.caption}
                />
              </CrossfadeImage>
            ),
        )}
      </ImageContainer>
      <Bottom ref={ref as React.RefObject<HTMLDivElement>}>
        <Left>
          <HeadlineWithSupertextComponent supertext="featuring" />
          <CurrentSource>
            {getCurrentSource(testimonials, activeIndex)}
          </CurrentSource>
          <NameList>
            {testimonials.map((testimonial, index) => (
              <Name
                onClick={() => jumpToTestimonial(index)}
                key={`testimonial-${testimonial.source}`}
                className={`${activeIndex === index ? 'active' : 'inactive'}`}
              >
                {testimonial.source}
              </Name>
            ))}
          </NameList>
        </Left>
        <Right>
          <TestimonialContainer tallest={tallest}>
            {getTestimonials(activeIndex, testimonials)}
          </TestimonialContainer>
        </Right>
      </Bottom>
      <Dots>
        {testimonials.map((_, index) => (
          <Dot
            key={`dot-${index}`}
            active={activeIndex === index}
            onClick={() => jumpToTestimonial(index)}
            className={`${activeIndex === index ? 'active' : 'inactive'}`}
          />
        ))}
      </Dots>
    </GroupTestimonial>
  );
});
